// ListSortContext.tsx
import React, { createContext, useContext, useState, ReactNode } from 'react';
import useSessionStorage from '../../hooks/useSessionStorage';
import { ListSortProps } from '../../utility/types';
import { useGetListSorts } from '../../hooks/api/ListSort/useGetListSorts';
import { ListSortContextType } from './DMContextInterfaces';

const ListSortContext = createContext<ListSortContextType | undefined>(undefined);

export function ListSortProvider({ children, incomingListSortId }: { children: ReactNode, incomingListSortId: string }) {
  const {
    data: fetchedListSorts,
    isLoading: isListSortsLoading,
    error: listSortsError,
  } = useGetListSorts();

  const { storedValue: sessionActiveListSortId, setValue: setSessionActiveListSortId } = useSessionStorage('activeListSort', incomingListSortId);
  const [ activeListSortId, setActiveListSortId ] = useState<string | null>(sessionActiveListSortId);

  // Compute activeListSort on-demand instead of storing it as state
  const activeListSort = activeListSortId && fetchedListSorts
    ? fetchedListSorts.find(ls => ls.id === activeListSortId) || null
    : null;

  const navigateToSelectedListSort = (listSort: ListSortProps) => {
    setActiveListSortId(listSort.id);
    setSessionActiveListSortId(listSort.id);

    const url = new URL(window.location.href);
    const newUrl = url.origin + '/decision-making/' + listSort.id;
    window.history.pushState({}, '', newUrl);
    window.location.reload();
  };

  return (
    <ListSortContext.Provider
      value={{
        listSorts: fetchedListSorts as ListSortProps[] || [],
        activeListSort: activeListSort as ListSortProps | null,
        activeListSortId,
        setActiveListSortId,
        isListSortsLoading,
        listSortsError,
        navigateToSelectedListSort,
      }}>
      {children}
    </ListSortContext.Provider>
  );
}

export function useListSortContext() {
  const ctx = useContext(ListSortContext);
  if (!ctx) throw new Error('useListSortContext must be used within ListSortProvider');
  return ctx;
}
